<template>
  <ValidationProvider
    v-slot="{ errors, validated }"
    :name="fieldName"
    rules="required"
    tag="div"
  >
    <div
      v-on-clickaway="closeMenu"
      :class="$style.dropDownContainer"
    >
      <IconButton
        v-model="selectedOptions"
        :class="[
          $style.dropDownButton,
          { [$style.errorOutline]: errors.length && !isMenuOpen }
        ]"
        hasOutline
        :icon="icon"
        iconPosition="right"
        :text="buttonText"
        type="secondary"
        @click="handleToggle"
      />

      <TransitionWrapper name="slideVerticalFloating">
        <div
          v-if="isMenuOpen"
          :class="$style.dropdownContent"
        >
          <LoadingIcon
            v-if="isLoading"
            :class="$style.loader"
            :isCloud="false"
            :scale="3"
          />
          <div v-else>
            <HeadingExtraSmall>
              Available ({{ options.length - selectedOptions.length }})
            </HeadingExtraSmall>

            <Listbox
              :options="sortedOptions"
              :showSearch="shouldShowSearch"
              :value="selectedOptions"
              @input="(selections) => $emit('input', selections)"
            />
          </div>
        </div>
      </TransitionWrapper>
    </div>

    <div
      v-if="!isMenuOpen && validated && errors.length"
      :class="$style.errorContainer"
    >
      <Icon
        :class="$style.errorIcon"
        icon="error"
        inline
        :scale="1"
      />
      <span :class="$style.message"> {{ errors[0] }}</span>
    </div>
  </ValidationProvider>
</template>

<script>
import {
  HeadingExtraSmall,
  Icon,
  IconButton,
  Listbox,
  LoadingIcon,
  TransitionWrapper,
} from '@jumpcloud/ui-components';
import { ValidationProvider } from 'vee-validate3';
import { directive as onClickaway } from 'vue-clickaway';
import { uniqBy } from 'lodash';

export default {
  name: 'DropdownListSearch',

  components: {
    HeadingExtraSmall,
    Icon,
    IconButton,
    Listbox,
    LoadingIcon,
    TransitionWrapper,
    ValidationProvider,
  },

  directives: {
    onClickaway,
  },

  props: {
    buttonText: {
      type: String,
      required: true,
    },
    fieldName: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => ([]),
    },
    selectedOptions: {
      type: Array,
      default: () => ([]),
    },
    shouldShowSearch: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isMenuOpen: false,
    };
  },

  computed: {
    icon() {
      return this.isMenuOpen ? 'caretUp' : 'caretDown';
    },

    sortedOptions() {
      return this.selectedOptions.length
        ? uniqBy([...this.selectedOptions, ...this.options], 'value')
        : this.options;
    },
  },

  methods: {
    closeMenu() {
      this.isMenuOpen = false;
    },

    handleToggle() {
      this.isMenuOpen = !this.isMenuOpen;
    },
  },
};
</script>

<style module>
.dropDownContainer.dropDownContainer {
  min-width: 10ch;
  position: relative;
  z-index: auto;
}

.dropDownButton.dropDownButton {
  border: var(--jc-border-width) solid var(--jc-border-color-medium);
  border-radius: var(--jc-border-radius);
  font-weight: 400;
  padding: 9px 12px;
  position: relative;
  width: 100%;
  z-index: 1;
}

.dropDownButton.dropDownButton:hover {
  background-color: #fff;
}

.dropDownButton.errorOutline {
  border: var(--jc-border-width) solid var(--jc-color-error-base);
}

/* Hacks to make IconButton match Select component */
.dropDownButton.dropDownButton > span {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.dropDownButton.dropDownButton > span > span > svg {
  height: 0.65em;
  width: 0.8em;
}

/** End hackiness */

.dropdownContent {
  background: #fff;
  border-radius: var(--jc-border-radius);
  box-shadow: var(--jc-box-shadow-card);
  margin: var(--jc-spacer-large) 0;
  padding: 1em;
  position: absolute;
  right: 0;
  top: 5px;
  width: 22em;
  z-index: 2;
}

.loader {
  display: block;
  margin: 1em auto;
}

.errorContainer {
  display: inline-flex;
}

.message {
  color: var(--jc-color-neutral-text-descriptive);
  display: flex;
  font-size: var(--jcBodySmall);
  line-height: 1.5;
  margin-top: var(--jc-spacer-x-small);
  min-height: var(--input-message-min-height, var(--jc-size-base));
}

.errorIcon {
  color: var(--jc-color-error-base);
  margin-top: var(--jc-spacer-x-small);
}
</style>
